import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLang } from '../i18n/translations';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ChevronRight, Clock, MapPin, CalendarDays, Landmark, Mountain, BedDouble,
  UtensilsCrossed, Bike, Snowflake, Waves, Church, ListChecks
} from 'lucide-react';

const ICONS = { Landmark, Mountain, BedDouble, UtensilsCrossed, Bike, Snowflake, Waves, Church };

const fmtDate = (d) => {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}.${m}.${y}.`;
};

export default function Home() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState({ offers: [], notices: [], events: [], types: [] });

  useEffect(() => {
    const load = async () => {
      try {
        const [o, n, e, t] = await Promise.all([
          api.get('/offers'), api.get('/notices'), api.get('/events'), api.get('/types')
        ]);
        setData({ offers: o.data, notices: n.data, events: e.data, types: t.data });
      } catch (err) {
        console.error('Greška pri učitavanju podataka:', err);
      }
    };
    load();
  }, []);

  const featured = data.offers.filter((o) => o.featured).slice(0, 4);
  const upcoming = [...data.events].sort((a, b) => a.date.localeCompare(b.date)).slice(0, 3);

  return (
    <div className="bg-white">
      {/* HERO - klon eprivreda.ba */}
      <section className="relative overflow-hidden hero-bg" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <Badge className="bg-[#e8ecf7] text-[#001752] hover:bg-[#e8ecf7] mb-5 px-3 py-1 text-xs tracking-wide font-semibold">
              IS E-TURIZAM
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-[#0b1a3d] tracking-tight mb-3">{t('heroSub1')}</h1>
            <p className="text-lg text-slate-500">{t('heroSub2')}</p>
            {t('heroSub3') && <p className="text-lg text-slate-500">{t('heroSub3')}</p>}
            <p className="text-slate-500 mt-4 max-w-md leading-relaxed">{t('heroDesc')}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button onClick={() => navigate('/ponuda')} data-testid="hero-explore-btn"
                className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md px-7 h-11 transition-colors duration-200">
                {t('exploreOffer')} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
              <Button onClick={() => navigate('/mapa')} variant="outline" data-testid="hero-map-btn"
                className="border-[#001752] text-[#001752] hover:bg-[#001752] hover:text-white rounded-md px-7 h-11 transition-colors duration-200">
                {t('viewMap')}
              </Button>
            </div>
          </div>
          <div className="hidden lg:flex justify-center animate-fade-up" style={{ animationDelay: '150ms' }}>
            <img src="/hero-map.png" alt="Srednjobosanski kanton" className="w-full max-w-xl" />
          </div>
        </div>
      </section>

      {/* STATISTIKA */}
      <section className="border-y border-slate-100 bg-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { n: '120+', l: t('stats1') },
            { n: '85+', l: t('stats2') },
            { n: '270+', l: t('stats3') },
            { n: '76.000+', l: t('stats4') }
          ].map((s, i) => (
            <div key={i} className="text-center">
              <p className="text-3xl lg:text-4xl font-bold text-[#001752]">{s.n}</p>
              <p className="text-sm text-slate-500 mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* KATEGORIJE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="types-section">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-[#0b1a3d]">{t('browseByType')}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {data.types.map((tp) => {
            const Icon = ICONS[tp.icon] || Landmark;
            return (
              <button key={tp.id} onClick={() => navigate(`/ponuda?tip=${tp.id}`)} data-testid={`type-card-${tp.id}`}
                className="group bg-white border border-slate-100 rounded-xl p-6 text-left hover:border-[#001752]/30 hover:shadow-lg hover:shadow-slate-200/60 transition-all duration-300">
                <div className="w-11 h-11 rounded-lg bg-[#e8ecf7] flex items-center justify-center mb-4 group-hover:bg-[#001752] transition-colors duration-300">
                  <Icon className="h-5 w-5 text-[#001752] group-hover:text-white transition-colors duration-300" />
                </div>
                <p className="font-semibold text-[#0b1a3d] text-sm leading-snug">{tp.name}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* IZDVOJENA PONUDA */}
      <section className="bg-[#f8f9fd] py-16" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <h2 className="text-3xl font-bold text-[#0b1a3d]">{t('featuredOffer')}</h2>
            <Button onClick={() => navigate('/ponuda')} data-testid="view-all-offers-btn"
              className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md transition-colors duration-200">
              {t('viewAllOffers')} <ListChecks className="h-4 w-4 ml-2" />
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((o) => (
              <Link key={o.id} to={`/ponuda/${o.id}`} data-testid={`featured-card-${o.id}`}
                className="group bg-white rounded-xl overflow-hidden border border-slate-100 hover:shadow-xl hover:shadow-slate-200/70 hover:-translate-y-1 transition-all duration-300">
                <div className="h-44 overflow-hidden relative">
                  <img src={o.image} alt={o.title} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute top-3 left-3 bg-white/95 text-[#001752] text-xs font-semibold px-2.5 py-1 rounded-md flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> {o.city}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-semibold text-[#0b1a3d] leading-snug mb-2">{o.title}</h3>
                  <p className="text-sm text-slate-500 line-clamp-2">{o.shortDesc}</p>
                  <span className="inline-flex items-center text-sm font-semibold text-[#001752] mt-4 group-hover:gap-2 gap-1 transition-all duration-200">
                    {t('details')} <ChevronRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* OBAVIJESTI - eprivreda.ba stil kartica */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="notices-section">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <h2 className="text-3xl font-bold text-[#0b1a3d]">{t('notices')}</h2>
          <Button onClick={() => navigate('/obavijesti')} data-testid="view-all-notices-btn"
            className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md transition-colors duration-200">
            {t('viewAllNotices')} <ListChecks className="h-4 w-4 ml-2" />
          </Button>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {data.notices.slice(0, 3).map((n) => (
            <div key={n.id} className="bg-white rounded-xl border border-slate-100 overflow-hidden hover:shadow-xl hover:shadow-slate-200/70 transition-shadow duration-300 flex flex-col" data-testid={`notice-card-${n.id}`}>
              <div className="notice-banner"><span>OBAVIJEST</span></div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-bold text-[#0b1a3d] uppercase text-sm leading-snug mb-3 line-clamp-2">{n.title}</h3>
                <p className="text-sm text-slate-500 line-clamp-3 flex-1">{n.excerpt}</p>
                <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-50">
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                    <Clock className="h-3.5 w-3.5" /> {t('published')}: {fmtDate(n.date)}
                  </span>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/obavijesti/${n.id}`)}
                    className="border-slate-200 text-[#001752] hover:bg-[#001752] hover:text-white rounded-md text-xs transition-colors duration-200">
                    {t('readMore')} <ChevronRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* DOGAĐAJI */}
      <section className="bg-[#f8f9fd] py-16" data-testid="events-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <h2 className="text-3xl font-bold text-[#0b1a3d]">{t('upcomingEvents')}</h2>
            <Button onClick={() => navigate('/kalendar')} data-testid="view-all-events-btn"
              className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md transition-colors duration-200">
              {t('viewAllEvents')} <CalendarDays className="h-4 w-4 ml-2" />
            </Button>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {upcoming.map((e) => (
              <div key={e.id} onClick={() => navigate('/kalendar')} data-testid={`event-card-${e.id}`}
                className="group bg-white rounded-xl overflow-hidden border border-slate-100 hover:shadow-xl hover:shadow-slate-200/70 hover:-translate-y-1 transition-all duration-300 cursor-pointer">
                <div className="h-40 overflow-hidden relative">
                  <img src={e.image} alt={e.title} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 left-3 bg-white rounded-lg px-3 py-1.5 text-center shadow-md">
                    <p className="text-lg font-bold text-[#001752] leading-none">{e.date.split('-')[2]}</p>
                    <p className="text-[10px] uppercase font-semibold text-slate-500 mt-0.5">
                      {['JAN','FEB','MAR','APR','MAJ','JUN','JUL','AUG','SEP','OKT','NOV','DEC'][parseInt(e.date.split('-')[1], 10) - 1]}
                    </p>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-xs font-semibold text-[#001752] uppercase tracking-wide mb-1">{e.category}</p>
                  <h3 className="font-semibold text-[#0b1a3d] leading-snug mb-2">{e.title}</h3>
                  <p className="text-sm text-slate-500 flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" /> {e.location}, {e.city}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
